import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import TerminalIcon from '@mui/icons-material/Terminal'
import SyncIcon from '@mui/icons-material/Sync'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import { addSeverityCounts, compareTriage, emptySeverityCounts } from '../../utils/dependencyTracker'
import { logRuntimeUpgradeReport, RUNTIME_STANDARDS, describeRuntimeStandards } from '../../utils/runtimeStandards'
import {
  SEV,
  SEV_INK,
  UPGRADE,
  UPGRADE_INK,
  SeverityBadge,
  StatusBadge
} from './boardBadges'
import { ClientBanner } from './clientBoardTree'
import { VulnExportDialog } from './vulnExportDialog'

const SORT_OPTIONS_VULN = [
  { value: 'severity-desc', label: 'High severity' },
  { value: 'severity-asc', label: 'Low severity' },
  { value: 'name-asc', label: 'A–Z' },
  { value: 'name-desc', label: 'Z–A' }
]

const SORT_OPTIONS_UPGRADE = [
  { value: 'upgrade-desc', label: 'Needs upgrade first' },
  { value: 'name-asc', label: 'A–Z' },
  { value: 'name-desc', label: 'Z–A' }
]

const clientKey = (client) => client?.id || client?.label || ''

const compareName = (a, b) => String(a?.label || '').localeCompare(String(b?.label || ''))

const sortClients = (list, sortBy) => {
  const next = [...list]
  switch (sortBy) {
    case 'name-asc':
      return next.sort(compareName)
    case 'name-desc':
      return next.sort((a, b) => compareName(b, a))
    case 'upgrade-desc':
      return next.sort((a, b) => {
        const au = a.needsUpgrade ? 1 : 0
        const bu = b.needsUpgrade ? 1 : 0
        if (bu !== au) return bu - au
        return compareName(a, b)
      })
    case 'severity-asc':
      return next.sort((a, b) => compareTriage(b, a))
    case 'severity-desc':
    default:
      return next.sort(compareTriage)
  }
}

const VulnClientBoard = ({
  tracked = [],
  loading = false,
  mode = 'vulnerabilities',
  onRefreshDt = null,
  onRefreshRepoDt = null,
  refreshingDt = false,
  refreshDtPhase = null,
  refreshingRepoUuid = null,
  onRefreshBitbucket = null,
  onRefreshRepoBitbucket = null,
  refreshingBitbucket = false,
  refreshBitbucketPhase = null,
  refreshingBitbucketRepoUuid = null
}) => {
  const isUpgrades = mode === 'upgrades'
  const sortOptions = isUpgrades ? SORT_OPTIONS_UPGRADE : SORT_OPTIONS_VULN
  const [query, setQuery] = useState('')
  const [sortBy, setSortBy] = useState(isUpgrades ? 'upgrade-desc' : 'severity-desc')
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [exportOpen, setExportOpen] = useState(false)
  const [needsUpgradeOnly, setNeedsUpgradeOnly] = useState(false)
  const [consoleHint, setConsoleHint] = useState('')
  const selectionInitialized = useRef(false)

  const refreshDtLabel = refreshDtPhase === 'loading-code-tree'
    ? 'Loading code tree…'
    : refreshDtPhase === 'syncing-dt'
      ? 'Syncing DT…'
      : refreshingDt
        ? 'Refreshing DT…'
        : 'Refresh from DT'

  const refreshBitbucketLabel = refreshBitbucketPhase === 'loading-code-tree'
    ? 'Loading code tree…'
    : refreshBitbucketPhase === 'syncing-bitbucket'
      ? 'Syncing Bitbucket…'
      : refreshingBitbucket
        ? 'Refreshing Bitbucket…'
        : 'Refresh Bitbucket'

  useEffect(() => {
    setSortBy(isUpgrades ? 'upgrade-desc' : 'severity-desc')
    setNeedsUpgradeOnly(false)
    setConsoleHint('')
    setExportOpen(false)
    selectionInitialized.current = false
  }, [isUpgrades])

  useEffect(() => {
    setSelectedIds((prev) => {
      const alive = new Set(tracked.map(clientKey).filter(Boolean))
      if (!selectionInitialized.current) {
        selectionInitialized.current = true
        return new Set()
      }
      return new Set([...prev].filter((id) => alive.has(id)))
    })
  }, [tracked])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    let base = !q
      ? tracked
      : tracked.filter((c) => String(c.label || '').toLowerCase().includes(q))
    if (isUpgrades && needsUpgradeOnly) base = base.filter((c) => c.needsUpgrade)
    return sortClients(base, sortBy)
  }, [tracked, query, sortBy, needsUpgradeOnly, isUpgrades])

  const selectedClients = useMemo(
    () => filtered.filter((c) => selectedIds.has(clientKey(c))),
    [filtered, selectedIds]
  )

  const selectedShownCount = selectedClients.length
  const allShownSelected = filtered.length > 0 && selectedShownCount === filtered.length
  const shownWithDt = filtered.filter((c) => c.hasDt).length
  const needsUpgradeShown = filtered.filter((c) => c.needsUpgrade).length
  const needsUpgradeTotal = tracked.filter((c) => c.needsUpgrade).length

  const displayedSeverity = useMemo(
    () => filtered.reduce((acc, c) => addSeverityCounts(acc, c.severityCounts), emptySeverityCounts()),
    [filtered]
  )

  const totalVulnsDisplayed = useMemo(
    () => filtered.reduce((sum, c) => sum + (Number(c.totalFindings) || 0), 0),
    [filtered]
  )

  const isFiltering = query.trim().length > 0 || (isUpgrades && needsUpgradeOnly)

  const runConsoleUpgradeCheck = () => {
    const result = logRuntimeUpgradeReport(filtered, {
      title: `Runtime upgrade check · ${filtered.length} shown`
    })
    setConsoleHint(
      `Console: ${result.behind.length} need upgrade · ${result.unknown.length} no/unknown version ` +
      `(${describeRuntimeStandards().dotnet} / ${describeRuntimeStandards().node})`
    )
  }

  const toggleClient = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const selectAllShown = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      filtered.forEach((c) => next.add(clientKey(c)))
      return next
    })
  }

  const clearShown = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      filtered.forEach((c) => next.delete(clientKey(c)))
      return next
    })
  }

  if (loading) return null

  let body
  if (!tracked.length) {
    body = (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: alpha('#fff', 0.85), mb: 0.5 }}>
          {isUpgrades
            ? 'No clients with upgrade tracking enabled'
            : 'No clients with vulnerability tracking enabled'}
        </Typography>
        <Typography variant='caption' sx={{ color: alpha('#fff', 0.5) }}>
          {isUpgrades
            ? 'Turn on upgradeTracking in SOCR to surface client banners here.'
            : 'Turn on vulnTracking in SOCR to surface client banners here.'}
        </Typography>
      </Box>
    )
  } else if (!filtered.length) {
    body = (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: alpha('#fff', 0.85), mb: 0.5 }}>
          {needsUpgradeOnly && !query.trim()
            ? 'No clients below runtime standards'
            : `No clients match “${query.trim()}”`}
        </Typography>
        <Typography variant='caption' sx={{ color: alpha('#fff', 0.5) }}>
          {needsUpgradeOnly
            ? 'Clear the upgrade filter or adjust standards in runtimeStandards.js.'
            : 'Clear the search to see all tracked clients again.'}
        </Typography>
      </Box>
    )
  } else {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {filtered.map((client) => {
          const id = clientKey(client)
          return (
            <ClientBanner
              key={id}
              client={client}
              mode={mode}
              selected={selectedIds.has(id)}
              onToggleSelect={(checked) => toggleClient(id, checked)}
              onRefreshRepoDt={onRefreshRepoDt}
              refreshingRepoUuid={refreshingRepoUuid}
              refreshDtDisabled={refreshingDt || refreshingBitbucket}
              onRefreshRepoBitbucket={onRefreshRepoBitbucket}
              refreshingBitbucketRepoUuid={refreshingBitbucketRepoUuid}
              refreshBitbucketDisabled={refreshingDt || refreshingBitbucket}
            />
          )
        })}
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          mb: 1.75
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between'
          }}
        >
          <TextField
            size='small'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search clients…'
            aria-label='Search clients'
            sx={{
              flex: '1 1 220px',
              minWidth: 0,
              maxWidth: 420,
              '& .MuiOutlinedInput-root': {
                backgroundColor: alpha('#000', 0.22),
                borderRadius: 1.5
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ fontSize: 18, color: alpha('#fff', 0.45) }} />
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <FormControl size='small' sx={{ minWidth: 150 }}>
              <InputLabel id='vuln-client-sort-label'>Sort</InputLabel>
              <Select
                labelId='vuln-client-sort-label'
                label='Sort'
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{
                  backgroundColor: alpha('#000', 0.22),
                  borderRadius: 1.5
                }}
              >
                {sortOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isUpgrades
              ? (
                <>
                  <Button
                    variant={needsUpgradeOnly ? 'contained' : 'outlined'}
                    size='small'
                    color='warning'
                    onClick={() => setNeedsUpgradeOnly((v) => !v)}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5 }}
                    title={`EOL-aware: warn ${RUNTIME_STANDARDS.dotnet.warnMonthsBeforeEol} months before support ends. Current target ${RUNTIME_STANDARDS.dotnet.target} / Node ${RUNTIME_STANDARDS.node.target}.`}
                  >
                    Needs upgrade{needsUpgradeTotal ? ` (${needsUpgradeTotal})` : ''}
                  </Button>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={(
                      <CloudSyncIcon
                        sx={{
                          animation: refreshingBitbucket ? 'bb-spin 0.9s linear infinite' : 'none',
                          '@keyframes bb-spin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                    )}
                    onClick={() => onRefreshBitbucket?.()}
                    disabled={!onRefreshBitbucket || refreshingBitbucket || refreshingDt}
                    title='Pull Bitbucket name/slug metadata into SOCR (fast), then reload the code tree. Per-repo sync starts a server-side runtime rescan without a multi-minute client HTTP hold.'
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5 }}
                  >
                    {refreshBitbucketLabel}
                  </Button>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<TerminalIcon />}
                    onClick={runConsoleUpgradeCheck}
                    disabled={!filtered.length}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Console check
                  </Button>
                </>
                )
              : (
                <>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={(
                      <SyncIcon
                        sx={{
                          animation: refreshingDt ? 'dt-spin 0.9s linear infinite' : 'none',
                          '@keyframes dt-spin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                    )}
                    onClick={() => onRefreshDt?.()}
                    disabled={!onRefreshDt || refreshingDt || refreshingBitbucket}
                    title='Sync DT project names into SOCR, then reload the code tree'
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5 }}
                  >
                    {refreshDtLabel}
                  </Button>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<FileDownloadIcon />}
                    disabled={!selectedShownCount}
                    onClick={() => setExportOpen(true)}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5, px: 1.5 }}
                  >
                    Export{selectedShownCount ? ` (${selectedShownCount})` : ''}
                  </Button>
                </>
                )}
          </Box>
        </Box>

        {consoleHint
          ? (
            <Typography variant='caption' sx={{ color: alpha('#ffb74d', 0.95), fontWeight: 700 }}>
              {consoleHint}
            </Typography>
            )
          : null}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800, color: alpha('#fff', 0.9), fontSize: '0.95rem' }}>
              Clients shown
              <Typography component='span' sx={{ ml: 0.75, color: alpha('#fff', 0.5), fontWeight: 700, fontSize: '0.8rem' }}>
                ({filtered.length}{isFiltering ? ` of ${tracked.length}` : ''})
              </Typography>
            </Typography>
            <StatusBadge
              dense
              label={`Has DT ${shownWithDt}`}
              color={UPGRADE.ok}
              ink={UPGRADE_INK.ok}
              hot={shownWithDt > 0}
            />
            <StatusBadge
              dense
              label={`No DT ${filtered.length - shownWithDt}`}
              color={UPGRADE.missing}
              ink={UPGRADE_INK.missing}
              hot={(filtered.length - shownWithDt) > 0}
            />
            {isUpgrades
              ? (
                <StatusBadge
                  dense
                  label={`Needs upgrade ${needsUpgradeShown}`}
                  color={UPGRADE.behind}
                  ink={UPGRADE_INK.behind}
                  hot={needsUpgradeShown > 0}
                />
                )
              : (
                <>
                  <Typography variant='caption' sx={{ color: alpha('#fff', 0.45), fontWeight: 700 }}>
                    {selectedShownCount} selected
                  </Typography>
                  <Button
                    size='small'
                    onClick={allShownSelected ? clearShown : selectAllShown}
                    disabled={!filtered.length}
                    sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0.75 }}
                  >
                    {allShownSelected ? 'Clear shown' : 'Select all shown'}
                  </Button>
                </>
                )}
          </Box>

          {!isUpgrades
            ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    color: alpha('#fff', 0.48),
                    mr: 0.25
                  }}
                >
                  Vulns displayed
                </Typography>
                <Chip
                  size='small'
                  label={totalVulnsDisplayed.toLocaleString()}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    backgroundColor: alpha('#fff', 0.1),
                    color: alpha('#fff', 0.9),
                    border: `1px solid ${alpha('#fff', 0.16)}`
                  }}
                />
                <SeverityBadge shortLabel='C' label='Critical' value={displayedSeverity.critical} color={SEV.critical} ink={SEV_INK.critical} dense />
                <SeverityBadge shortLabel='H' label='High' value={displayedSeverity.high} color={SEV.high} ink={SEV_INK.high} dense />
                <SeverityBadge shortLabel='M' label='Medium' value={displayedSeverity.medium} color={SEV.medium} ink={SEV_INK.medium} dense />
                <SeverityBadge shortLabel='L' label='Low' value={displayedSeverity.low} color={SEV.low} ink={SEV_INK.low} dense />
              </Box>
              )
            : null}
        </Box>
      </Box>

      <Box
        sx={{
          maxHeight: { xs: '60vh', md: 'calc(100vh - 230px)' },
          overflowY: 'auto',
          overflowX: 'hidden',
          borderRadius: 2,
          border: `1px solid ${alpha('#fff', 0.1)}`,
          backgroundColor: alpha('#000', 0.2),
          backgroundImage: `linear-gradient(180deg, ${alpha('#fff', 0.03)} 0%, transparent 120px)`,
          p: { xs: 1, sm: 1.25 },
          pr: { xs: 0.75, sm: 1 },
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha('#fff', 0.25)} transparent`,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#fff', 0.22),
            borderRadius: 8
          }
        }}
      >
        {body}
      </Box>

      {!isUpgrades
        ? (
          <VulnExportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            clients={selectedClients}
          />
          )
        : null}
    </Box>
  )
}

export { VulnClientBoard }
